import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus, Users, Lock, Globe, Shield } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import CommunityEditorDialog from "@/components/CommunityEditorDialog";

const DEFAULT_THEME = { join: "#0A84FF", settings: "#12466F", leave: "#EF4444" };

/**
 * Contact-card style community tile.  Replaces the old bubble grid: each
 * community shows its banner, name, description, member count and a public/
 * private badge.  Clicking anywhere on the card navigates to /community/:id
 * (full page).
 */
const CommunityCard = ({ c, onOpen }) => {
  const t = c.theme || DEFAULT_THEME;
  return (
    <button
      onClick={onOpen}
      data-testid={`community-card-${c.id}`}
      className="ios-card overflow-hidden text-left transition-all hover:-translate-y-0.5 hover:shadow-lg group p-0"
    >
      {/* Banner header ---------------------------------------------- */}
      <div
        className="relative h-32 w-full"
        style={{
          background: c.bg_image_url
            ? `url(${c.bg_image_url}) center/cover no-repeat`
            : `linear-gradient(135deg, ${t.settings}, ${t.join})`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
        <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-white/85 backdrop-blur px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold text-slate-800">
          {c.is_public ? <><Globe className="w-2.5 h-2.5" /> Pubblica</> : <><Lock className="w-2.5 h-2.5" /> Su richiesta</>}
        </div>
        {c.is_owner && (
          <div className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-amber-500 text-white px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold shadow">
            <Shield className="w-2.5 h-2.5" /> Moderatore
          </div>
        )}
      </div>
      {/* Body ------------------------------------------------------- */}
      <div className="p-4">
        <div className="font-display font-bold text-base truncate">{c.name}</div>
        {c.description ? (
          <p className="text-xs text-slate-500 mt-1 line-clamp-2 min-h-[2rem]">{c.description}</p>
        ) : (
          <p className="text-xs text-slate-400 italic mt-1 min-h-[2rem]">Nessuna descrizione</p>
        )}
        <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {c.member_count} membri</span>
          {c.is_member ? (
            <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold">Iscritto</span>
          ) : (
            <span className="rounded-full bg-slate-100 text-slate-500 px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold group-hover:bg-[#007AFF] group-hover:text-white transition-colors">Apri</span>
          )}
        </div>
      </div>
    </button>
  );
};

const CommunitiesSection = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/communities"); setItems(r.data || []); }
    catch { toast.error("Errore caricamento community"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="ios-card p-5 mb-6" data-testid="communities-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">Community</div>
          <div className="font-display font-bold text-xl mt-0.5">Le nostre community</div>
          <p className="text-xs text-slate-500 mt-1">Clicca una community per aprirla, gestire i membri o pubblicare contenuti.</p>
        </div>
        <Button onClick={() => setEditorOpen(true)} className="rounded-full bg-slate-900 text-white" data-testid="community-create">
          <Plus className="w-4 h-4 mr-1.5" /> Nuova community
        </Button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-slate-500 text-sm">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-slate-500 text-sm">
          <div className="font-semibold text-slate-700">Nessuna community ancora</div>
          <div className="text-xs mt-1">Sei il primo? Crea la community che vorresti frequentare.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="communities-grid">
          {items.map((c) => (
            <CommunityCard key={c.id} c={c} onOpen={() => navigate(`/community/${c.id}`)} />
          ))}
        </div>
      )}

      <CommunityEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        community={null}
        onSaved={(saved) => {
          load();
          // After creating a community, jump to its detail page so the user
          // can immediately upload a banner or invite people.
          if (saved?.id) navigate(`/community/${saved.id}`);
        }}
      />
    </div>
  );
};

export default CommunitiesSection;
